import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSessionStore } from '@/store/sessionStore';
import { cn, getInitials, stringToColor } from '@/lib/utils';
import { noticesApi, studentApi, authApi, instituteAdminApi } from '@/lib/api';
import { canRoleAccessPath, filterNavItemsByPermissions, getFirstAccessiblePath, institutePortalRoles, type AccessPermissionsPayload } from '@/lib/accessControl';
import { getBrandContactLine, getBrandDisplayName, getBrandShortName } from '@/lib/instituteBranding';
import PageErrorBoundary from '@/components/PageErrorBoundary';
import { getExeRelayStats, getExeSyncStatus, getRelayBaseUrl, isElectronServerRuntime, triggerExeSync } from '@/lib/electronBridge';
import { useTheme } from '@/hooks/useTheme';
import ReportAdminModal from '@/components/ReportAdminModal';
import UpdateReadyBanner from '@/components/UpdateReadyBanner';

// Icons (inline SVG for simplicity)
const icons = {
    dashboard: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
    ),
    users: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
    ),
    building: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
    ),
    document: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    ),
    clipboard: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
    ),
    chart: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
    ),
    test: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    ),
    history: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    ),
    rubric: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
    ),
    assignment: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
    ),
    logout: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
    ),
    menu: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
    ),
    close: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    ),
    masterData: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
    ),
    revenue: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    ),
    notice: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    ),
    settings: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    ),
    // Institute Admin Module Icons
    liveStudents: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    ),
    addStudent: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
    ),
    finance: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    ),
    sessions: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
    ),
    attendance: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
    ),
    register: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
    ),
    staff: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    ),
};

const superAdminNavItems = [
    { path: '/admin', icon: icons.dashboard, label: 'Dashboard', end: true },
    {
        label: 'Institutes',
        icon: icons.building,
        isDropdown: true,
        children: [
            { path: '/admin/institutes', label: 'All Institutes', matchNested: true },
            { path: '/admin/institutes/add', label: 'Add Institute' },
            { path: '/admin/users', label: 'Administrators' },
        ],
    },
    {
        label: 'Academics',
        icon: icons.document,
        isDropdown: true,
        children: [
            { path: '/admin/questions', label: 'Question Papers' },
            { path: '/admin/exam-rules', label: 'Practice Rules' },
            // Where the platform's own guided-tutor chapters are reviewed and
            // published. Unlisted, the course could only be published by hand.
            { path: '/admin/tutor-curriculum', label: 'Guided Tutor Course' },
        ],
    },
    {
        label: 'Revenue',
        icon: icons.revenue,
        isDropdown: true,
        children: [
            { path: '/admin/revenue', label: 'Revenue Dashboard' },
            { path: '/admin/transactions', label: 'Transactions' },
        ],
    },
    {
        label: 'Reports & Analytics',
        icon: icons.chart,
        isDropdown: true,
        children: [
            { path: '/admin/analytics', label: 'Analytics' },
            { path: '/admin/students-report', label: 'Students Report' },
            { path: '/admin/instructors-report', label: 'Instructors Report' },
        ],
    },
    {
        label: 'Content & Notices',
        icon: icons.notice,
        isDropdown: true,
        children: [
            { path: '/admin/notices', label: 'Notices' },
            { path: '/admin/content-library', label: 'Content Library' },
            { path: '/admin/ebooks-videos', label: 'E-books & Videos' },
        ],
    },
    {
        label: 'System',
        icon: icons.settings,
        isDropdown: true,
        children: [
            { path: '/admin/master-data', label: 'Master Data' },
            { path: '/admin/document-templates', label: 'Document Templates' },
            { path: '/admin/access-permissions', label: 'Roles & Permissions' },
            { path: '/admin/payment-gateway', label: 'License Payment Gateway' },
            { path: '/admin/settings', label: 'Settings & Integrations' },
            { path: '/admin/system-health', label: 'System Health' },
            { path: '/admin/version-management', label: 'Version Management' },
            { path: '/admin/remote-console', label: 'Remote Console' },
            { path: '/admin/error-reports', label: 'Error Reports' },
            { path: '/admin/lab-logins', label: 'Lab Logins' },
        ],
    },
];

const instituteAdminNavItems = [
    { path: '/admin/institute-dashboard', icon: icons.liveStudents, label: 'Dashboard', end: true },
    { path: '/admin/principal-dashboard', icon: icons.chart, label: 'Leadership Dashboard', end: true },
    { path: '/admin/front-office', icon: icons.addStudent, label: 'Front Office', end: true },
    { path: '/admin/counselor-dashboard', icon: icons.addStudent, label: 'Admissions', end: true },
    { path: '/admin/hr-dashboard', icon: icons.users, label: 'HR Workspace', end: true },
    { path: '/admin/receptionist', icon: icons.addStudent, label: 'Reception Desk', end: true },
    { path: '/admin/senior-instructor-dashboard', icon: icons.chart, label: 'Supervision', end: true },
    {
        label: 'Student Management',
        icon: icons.users,
        isDropdown: true,
        children: [
            { path: '/admin/student-register', label: 'Student Register' },
            { path: '/admin/students', label: 'All Students' },
            { path: '/admin/student-registration', label: 'Add Student' },
            { path: '/admin/attendance-report', label: 'Attendance Report' },
            { path: '/admin/general-register', label: 'General Register' },
        ],
    },
    {
        label: 'Academic Setup',
        icon: icons.masterData,
        isDropdown: true,
        children: [
            { path: '/admin/master-data', label: 'Master Data' },
            { path: '/admin/sessions', label: 'Sessions & Batches' },
            { path: '/admin/questions', label: 'Questions & Content' },
        ],
    },
    {
        label: 'Content Library',
        icon: icons.document,
        isDropdown: true,
        children: [
            { path: '/admin/content-library', label: 'Question Papers & Content' },
            // Where an instructor writes typing lessons and theory tests. The
            // page works and the route existed, but nothing in the product
            // linked to it: it could only be reached by typing the URL.
            { path: '/admin/instructor-content', label: 'Author Lessons & Tests' },
            // An institute sees the platform's chapters here read-only, authors
            // and publishes its own, and can keep a platform chapter out of its
            // own students' list without deleting it for anyone else.
            { path: '/admin/tutor-curriculum', label: 'Guided Tutor Course' },
            // Where each of this institute's students has reached in that
            // course, and the instruction they are stuck on.
            { path: '/admin/tutor-progress', label: 'Guided Tutor Progress' },
        ],
    },
    {
        label: 'Finance',
        icon: icons.finance,
        isDropdown: true,
        children: [
            // The student billing desk: search a student, see their enrollments
            // and history, take a payment. It had no menu entry at all and was
            // reachable only by being redirected there from another page --
            // the one screen in this group an institute uses every day.
            { path: '/admin/finance', label: 'Student Finance Workspace' },
            { path: '/admin/fee-collection', label: 'Fee Collection' },
            { path: '/admin/installment-tracker', label: 'Installment Tracker' },
            { path: '/admin/finance-analytics', label: 'Income vs Payable Analytics' },
            { path: '/admin/invoice-list', label: 'Invoice List' },
            { path: '/admin/payment-transactions', label: 'Payment Transactions' },
            { path: '/admin/license-payables', label: 'License Payables' },
            { path: '/admin/finance-exports', label: 'Export Centre' },
        ],
    },
    {
        label: 'Analytics',
        icon: icons.chart,
        isDropdown: true,
        children: [
            { path: '/admin/institute-analytics', label: 'Performance Analytics' },
        ],
    },
    {
        label: 'Lab & Devices',
        icon: icons.sessions,
        isDropdown: true,
        children: [
            { path: '/admin/lab-monitoring', label: 'Lab Monitoring' },
            { path: '/admin/live-class', label: 'Live Class' },
            // Start a class, take the register and enter marks. Same story as
            // the authoring page above: built, routed, never linked.
            { path: '/admin/class-room', label: 'Class Room' },
            { path: '/admin/performance-monitor', label: 'Performance Monitor' },
            { path: '/admin/lab-devices', label: 'Lab Devices & Keys' },
            { path: '/admin/device-license', label: 'Device License Summary' },
            { path: '/admin/exe-sync', label: 'EXE Sync Monitor' },
            { path: '/admin/software-downloads', label: 'Download Software' },
        ],
    },
    {
        label: 'Administration',
        icon: icons.staff,
        isDropdown: true,
        children: [
            { path: '/admin/approvals', label: 'Approvals Queue' },
            { path: '/admin/content-review', label: 'Content Review Queue' },
            { path: '/admin/staff', label: 'Staff' },
            { path: '/admin/notices', label: 'Notices' },
            { path: '/admin/settings', label: 'Account Settings' },
        ],
    },
];

// Dropdown navigation component
function NavDropdown({ item, onChildClick }: { item: any; onChildClick: () => void }) {
    const location = useLocation();
    const isChildActive = item.children?.some((child: any) =>
        child.matchNested
            ? location.pathname === child.path || location.pathname.startsWith(child.path + '/')
            : location.pathname === child.path
    );
    const [isOpen, setIsOpen] = useState(Boolean(isChildActive));

    useEffect(() => {
        if (isChildActive) {
            setIsOpen(true);
        }
    }, [isChildActive]);

    return (
        <div className="space-y-1">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'sidebar-link w-full justify-between',
                    isChildActive && 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                )}
            >
                <div className="flex items-center gap-3">
                    {item.icon}
                    <span>{item.label}</span>
                </div>
                <svg
                    className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="ml-4 pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-1">
                    {item.children?.map((child: any) => {
                        // For matchNested items, compute active state manually so that sibling
                        // paths (e.g. /admin/institutes/add) do NOT activate /admin/institutes
                        const siblingPaths: string[] = (item.children || [])
                            .filter((c: any) => c !== child)
                            .map((c: any) => c.path as string);
                        const isActive = child.matchNested
                            ? (location.pathname === child.path ||
                               (location.pathname.startsWith(child.path + '/') &&
                                !siblingPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))))
                            : location.pathname === child.path;
                        return (
                            <NavLink
                                key={child.path}
                                to={child.path}
                                end
                                className={() =>
                                    cn(
                                        'block px-3 py-2 text-sm rounded-lg transition-colors',
                                        isActive
                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                    )
                                }
                                onClick={onChildClick}
                            >
                                {child.label}
                            </NavLink>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function DashboardLayout() {
    const exeRuntime = isElectronServerRuntime();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [reportInstitute, setReportInstitute] = useState<{ name?: string; id?: string | null }>({});
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [studentExamNav, setStudentExamNav] = useState<any[]>([]);
    const [brandingName, setBrandingName] = useState('Institute Portal');
    const [brandingDisplayName, setBrandingDisplayName] = useState('Institute Portal');
    const [brandingLogo, setBrandingLogo] = useState('');
    const [brandingMeta, setBrandingMeta] = useState('');
    const [accessPermissions, setAccessPermissions] = useState<AccessPermissionsPayload | null>(null);
    const [exeSyncStatus, setExeSyncStatus] = useState<any>(null);
    const [exeRelayStats, setExeRelayStats] = useState<any>(null);
    const [syncingNow, setSyncingNow] = useState(false);
    const { user, logout } = useAuthStore();
    const sessions = useSessionStore((s) => s.sessions);
    const activeSessionId = useSessionStore((s) => s.sessionId);
    const sessionsLoaded = useSessionStore((s) => s.loaded);
    const loadSessions = useSessionStore((s) => s.loadSessions);
    const setSessionId = useSessionStore((s) => s.setSessionId);
    const resetSessionScope = useSessionStore((s) => s.reset);
    const navigate = useNavigate();
    const location = useLocation();
    const { isDark, toggleTheme } = useTheme();

    useEffect(() => {
        fetchNotifications();
        loadBranding();
    }, []);

    useEffect(() => {
        if (user?.role === 'student') {
            void loadStudentExamNav();
        }
    }, [user?.role]);

    useEffect(() => {
        if (!user?.role || !institutePortalRoles.has(user.role)) {
            setAccessPermissions(null);
            return;
        }

        instituteAdminApi.getAccessPermissions()
            .then((payload) => setAccessPermissions(payload || null))
            .catch(() => setAccessPermissions(null));
    }, [user?.role]);

    // Global admission-session scope — load the institute's allotted sessions
    // once so the header selector (and every page's session_id) has a value.
    useEffect(() => {
        if (!user?.role || !institutePortalRoles.has(user.role)) {
            if (sessionsLoaded) resetSessionScope();
            return;
        }
        if (!sessionsLoaded) void loadSessions();
    }, [user?.role, sessionsLoaded, loadSessions, resetSessionScope]);

    // Institute identity for the Report-Admin form: in the Server EXE it comes
    // from the bound server license; on the web it falls back to branding / user.
    useEffect(() => {
        const exe = (typeof window !== 'undefined' ? (window as any).examClient?.server : undefined);
        if (exe?.getInstituteInfo) {
            exe.getInstituteInfo()
                .then((r: any) => { if (r?.success) setReportInstitute({ name: r.data?.institute_name || undefined, id: r.data?.institute_id || null }); })
                .catch(() => {});
        } else {
            setReportInstitute({ name: brandingName !== 'Institute Portal' ? brandingName : undefined, id: (user as any)?.institute_id || null });
        }
    }, [brandingName, user]);

    useEffect(() => {
        if (!exeRuntime || user?.role === 'student') return;

        let cancelled = false;
        const loadExeShellStatus = async () => {
            try {
                const [syncStatus, relayStats] = await Promise.all([
                    getExeSyncStatus(),
                    getExeRelayStats(),
                ]);
                if (!cancelled) {
                    setExeSyncStatus(syncStatus);
                    setExeRelayStats(relayStats);
                }
            } catch {
                if (!cancelled) {
                    setExeSyncStatus(null);
                    setExeRelayStats(null);
                }
            }
        };

        loadExeShellStatus();
        const timer = window.setInterval(loadExeShellStatus, 8000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [exeRuntime, user?.role]);

    const loadBranding = async () => {
        try {
            const isInstituteScoped = Boolean(user?.role && institutePortalRoles.has(user.role));
            const publicConfig = await authApi.getPublicConfig();
            const appName = publicConfig?.branding?.display_name || publicConfig?.branding?.name || 'Institute Portal';
            const appLogo = publicConfig?.branding?.logo_url || '';

            if (isInstituteScoped) {
                const instConfig = await instituteAdminApi.getSystemConfig();
                const instName = getBrandShortName(instConfig);
                const instDisplayName = getBrandDisplayName(instConfig);
                const instLogo = instConfig?.company_logo;
                const instMeta = getBrandContactLine(instConfig) || (instConfig?.address ? String(instConfig.address) : '');
                setBrandingName(instName || appName);
                setBrandingDisplayName(instDisplayName || instName || appName);
                setBrandingLogo(instLogo || appLogo);
                setBrandingMeta(instMeta);
            } else {
                setBrandingName(appName);
                setBrandingDisplayName(appName);
                setBrandingLogo(appLogo);
                setBrandingMeta('');
            }

            if (appLogo) {
                const link = (document.querySelector("link[rel~='icon']") as HTMLLinkElement) || document.createElement('link');
                link.rel = 'icon';
                link.href = appLogo;
                document.head.appendChild(link);
            }
        } catch {
            // keep defaults on failure
        }
    };

    const fetchNotifications = async () => {
        try {
            const [data, count] = await Promise.all([
                noticesApi.list('received', { unreadOnly: true }),
                noticesApi.getUnreadCount(),
            ]);
            setNotifications((data || []).slice(0, 5));
            setUnreadCount(count || 0);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const loadStudentExamNav = async () => {
        try {
            const catalog = await studentApi.getExamCatalog();
            setStudentExamNav([
                { path: '/student', icon: icons.dashboard, label: 'Dashboard', end: true },
                { path: '/student/profile', icon: icons.users, label: 'Profile' },
                {
                    label: 'Course',
                    icon: icons.document,
                    isDropdown: true,
                    children: [
                        { path: '/student/typing-practice', label: 'Typing Practice' },
                        { path: '/student/course/theory', label: 'Theory Practice' },
                    ],
                },
                {
                    label: 'Typing Test',
                    icon: icons.test,
                    isDropdown: true,
                    children: [
                        { path: '/student/typing-test/all', label: 'All Tests' },
                        ...(catalog.typing_types || []).map((item: any) => ({
                            path: `/student/typing-test/${item.slug}`,
                            label: item.name,
                        })),
                    ],
                },
                {
                    label: 'Theory Test',
                    icon: icons.clipboard,
                    isDropdown: true,
                    children: [
                        { path: '/student/theory-test/all', label: 'All Tests' },
                        ...(catalog.theory_categories || []).map((item: any) => ({
                            path: `/student/theory-test/${item.slug}`,
                            label: item.name,
                        })),
                    ],
                },
                { path: '/student/demo-exam', icon: icons.assignment, label: 'Demo & Practice' },
                { path: '/student/results', icon: icons.chart, label: 'Results' },
            ]);
        } catch (error) {
            console.error('Failed to load student exam navigation:', error);
            setStudentExamNav([
                { path: '/student', icon: icons.dashboard, label: 'Dashboard', end: true },
                { path: '/student/profile', icon: icons.users, label: 'Profile' },
                { path: '/student/typing-test/all', icon: icons.test, label: 'Typing Test' },
                { path: '/student/theory-test/all', icon: icons.clipboard, label: 'Theory Test' },
                { path: '/student/results', icon: icons.chart, label: 'Results' },
            ]);
        }
    };

    // Select navigation items based on user role
    const getNavItems = () => {
        if (user?.role === 'student') {
            return studentExamNav;
        } else if (user?.role === 'super_admin') {
            return superAdminNavItems;
        }
        const items = filterNavItemsByPermissions(instituteAdminNavItems as any, accessPermissions, user?.role);
        // Server Manager is a Server-EXE-only section. It is appended AFTER
        // permission filtering so a stale synced permission catalog can never
        // hide the EXE's own operational pages (sync, relay, device keys).
        if (exeRuntime) {
            const serverManagerChildren = [
                { path: '/admin/exe-sync', label: 'Sync Manager' },
                { path: '/admin/lab-devices', label: 'Lab Devices & Keys' },
                { path: '/admin/lab-monitoring', label: 'Lab Monitoring' },
                { path: '/admin/live-class', label: 'Live Class' },
                { path: '/admin/performance-monitor', label: 'Performance Monitor' },
                { path: '/admin/batch-reschedule', label: 'Batch Reschedule' },
            ];
            // These pages also live under the base "Lab & Devices" dropdown — strip
            // them there so the EXE doesn't show each entry twice (the "double
            // double" menu). Drop any dropdown left empty by the dedup.
            const smPaths = new Set(serverManagerChildren.map((c) => c.path));
            const deduped = (items as any[])
                .map((item) => item?.children
                    ? { ...item, children: item.children.filter((ch: any) => !smPaths.has(ch.path)) }
                    : item)
                .filter((item) => !item?.children || item.children.length > 0);
            return [
                ...deduped,
                {
                    label: 'Server Manager',
                    icon: icons.sessions,
                    isDropdown: true,
                    children: serverManagerChildren,
                },
            ];
        }
        return items;
    };

    const navItems = getNavItems();
    const userName = (user?.profile?.first_name || user?.profile?.last_name)
        ? `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim()
        : user?.email?.split('@')[0] || 'Admin';
    const roleLabel = user?.role
        ? user.role
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
        : 'Administrator';

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const noticesDestination = user?.role === 'student' ? '/student' : '/admin/notices';
    const relayBaseUrl = getRelayBaseUrl(exeRelayStats);
    const handleExeSyncNow = async (full = false) => {
        setSyncingNow(true);
        try {
            await triggerExeSync(full);
            const [syncStatus, relayStats] = await Promise.all([getExeSyncStatus(), getExeRelayStats()]);
            setExeSyncStatus(syncStatus);
            setExeRelayStats(relayStats);
        } finally {
            setSyncingNow(false);
        }
    };

    useEffect(() => {
        if (!user?.role || !institutePortalRoles.has(user.role)) return;
        if (!accessPermissions) return;
        // Server Manager pages are always reachable inside the Server EXE,
        // regardless of what the (possibly stale) synced permission catalog says.
        if (exeRuntime && ['/admin/exe-sync', '/admin/lab-devices', '/admin/lab-monitoring', '/admin/performance-monitor', '/admin/batch-reschedule'].some(
            (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
        )) return;
        if (canRoleAccessPath(accessPermissions, user.role, location.pathname)) return;

        const fallbackPath = getFirstAccessiblePath(navItems as any);
        if (fallbackPath && fallbackPath !== location.pathname) {
            navigate(fallbackPath, { replace: true });
        }
    }, [accessPermissions, location.pathname, navigate, navItems, user?.role]);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={cn(
                    // Bento: on large screens the sidebar detaches from the edge and
                    // floats as its own rounded clay tile; on mobile it stays a full
                    // height drawer so the slide-in overlay still covers the screen.
                    'fixed top-0 left-0 z-50 h-full w-72 overflow-hidden bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-transform duration-300 lg:translate-x-0 shadow-sm',
                    'lg:top-4 lg:left-4 lg:h-[calc(100vh-2rem)] lg:rounded-3xl lg:border-r-0',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                {/* Sidebar Header */}
                <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-primary-100 bg-primary-50 text-primary-800 dark:border-primary-900 dark:bg-primary-950/30 dark:text-primary-300 overflow-hidden">
                            {brandingLogo ? (
                                <img src={brandingLogo} alt={brandingName} className="h-full w-full object-contain p-1" />
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="font-serif text-lg font-bold leading-tight text-slate-900 break-words dark:text-white">
                                {brandingName}
                            </h1>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 break-words">
                                {roleLabel}
                            </p>
                        </div>
                    </div>
                    <button
                        className="lg:hidden shrink-0 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close menu"
                    >
                        {icons.close}
                    </button>
                </div>

                {/* Navigation */}
                {/* Height is a percentage of the aside, not the viewport: the aside is
                    shorter than 100vh once it floats as a bento tile on lg+. */}
                <nav className="max-h-[calc(100%-196px)] space-y-1 overflow-y-auto p-4">
                    {navItems.map((item: any, index) => (
                        item.isDropdown ? (
                            <NavDropdown
                                key={index}
                                item={item}
                                onChildClick={() => setSidebarOpen(false)}
                            />
                        ) : (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.end}
                                className={({ isActive }) =>
                                    cn('sidebar-link', isActive && 'active')
                                }
                                onClick={() => setSidebarOpen(false)}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </NavLink>
                        )
                    ))}
                </nav>

                {/* User Info */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/40">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Signed In User</div>
                        <div className="flex items-center gap-3">
                        {user?.profile?.avatar_url ? (
                            <img
                                src={user.profile.avatar_url}
                                alt={userName}
                                className="h-10 w-10 rounded-full object-cover ring-2 ring-white/70"
                            />
                        ) : (
                            <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                                style={{ backgroundColor: stringToColor(userName) }}
                            >
                                {getInitials(user?.profile?.first_name || '', user?.profile?.last_name || '')}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate" title={String(userName || '')}>
                                {userName}
                            </p>
                            <p className="text-xs text-slate-500 truncate" title={String(user?.email || '')}>{user?.email}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 text-slate-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10 rounded-lg transition-colors"
                            title="Logout"
                        >
                            {icons.logout}
                        </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            {/* pl matches the floating dock: 4 (left inset) + 72 (width) + 4 (gap) */}
            <div className="lg:pl-[20rem] lg:pr-4">
                {/* Top Bar — a floating clay tray on lg+, flush strip on mobile */}
                <header className="sticky top-0 z-30 overflow-hidden border-b border-slate-200 bg-white/92 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/92 lg:top-4 lg:mt-4 lg:rounded-3xl lg:border-b-0">
                    <div className="flex items-center justify-between h-full px-4 lg:px-6">
                        <button
                            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                        >
                            {icons.menu}
                        </button>

                        <div className="flex-1 py-3">
                            <div className="hidden lg:block">
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Administrative Control Portal</p>
                                <p className="text-sm text-slate-700 dark:text-slate-300">Institute Management and Monitoring System</p>
                            </div>
                        </div>

                        {/* Right side actions */}
                        <div className="flex items-center gap-4">
                            {/* Global admission-session scope — everything on the
                                portal (students, fees, reports, registers) shows
                                data for the selected session only. */}
                            {user?.role && institutePortalRoles.has(user.role) && sessions.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <span className="hidden xl:inline text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Session</span>
                                    <select
                                        value={activeSessionId}
                                        onChange={(e) => setSessionId(e.target.value)}
                                        title="Admission session — every page on the portal shows this session only. A session with few students will make most pages look empty."
                                        className="max-w-[13rem] rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                        {/* The student count is the point of this
                                            label. Every page is filtered to the
                                            chosen session and nothing said so, so a
                                            session holding 3 of an institute's 47
                                            students made page after page look
                                            broken instead of filtered. */}
                                        {sessions.map((s: any) => (
                                            <option key={s.id} value={s.id}>
                                                {`${s.session_name || 'Session'}${s.year ? ` (${s.year})` : ''}`
                                                    + (typeof s.student_count === 'number' ? ` — ${s.student_count} student${s.student_count === 1 ? '' : 's'}` : '')
                                                    + (s.is_current ? ' • current' : '')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {exeRuntime && user?.role !== 'student' && (
                                <div className="hidden xl:flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/exe-sync')}
                                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                        title={relayBaseUrl || 'Relay not active'}
                                    >
                                        {exeRelayStats?.active ? `Relay :${exeRelayStats.port}` : 'Relay Off'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/exe-sync')}
                                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                    >
                                        Outbox {exeSyncStatus?.outboxCount ?? 0}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/exe-sync')}
                                        className={cn(
                                            'rounded-full border px-3 py-2 text-xs font-semibold transition',
                                            (exeSyncStatus?.conflictCount ?? 0) > 0
                                                ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300'
                                                : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                        )}
                                    >
                                        Conflicts {exeSyncStatus?.conflictCount ?? 0}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleExeSyncNow(false)}
                                        disabled={syncingNow}
                                        className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300"
                                    >
                                        {syncingNow ? 'Syncing...' : 'Sync Now'}
                                    </button>
                                </div>
                            )}
                            {/* Theme toggle */}
                            {user?.role && institutePortalRoles.has(user.role) && (
                                <button
                                    type="button"
                                    title="Report a problem to the platform admin"
                                    onClick={() => setShowReport(true)}
                                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                                    </svg>
                                    <span className="hidden lg:inline">Report Admin</span>
                                </button>
                            )}

                            <button
                                type="button"
                                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
                            >
                                {isDark ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>

                            {/* Notifications Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                                    aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
                                    aria-expanded={showNotifications}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1 right-1 w-2 h-2 bg-error-500 rounded-full" />
                                    )}
                                </button>

                                {showNotifications && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowNotifications(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-20 overflow-hidden animate-in fade-in zoom-in slide-in-from-top-2">
                                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                                <h3 className="font-semibold text-slate-900 dark:text-white">Recent Notices</h3>
                                                <button
                                                    onClick={() => navigate(noticesDestination)}
                                                    className="text-xs text-primary-600 hover:underline"
                                                >
                                                    View All
                                                </button>
                                            </div>
                                            <div className="max-h-96 overflow-y-auto">
                                                {notifications.length > 0 ? (
                                                    notifications.map((n) => (
                                                        <div
                                                            key={n.id}
                                                            className="p-4 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                                                            onClick={async () => {
                                                                setShowNotifications(false);
                                                                if (!n.is_read) {
                                                                    await noticesApi.markAsRead(n.id);
                                                                    setNotifications((prev) => prev.filter((notice) => notice.id !== n.id));
                                                                    setUnreadCount((prev) => Math.max(prev - 1, 0));
                                                                }
                                                                navigate(noticesDestination);
                                                            }}
                                                        >
                                                            <div className="flex justify-between items-start mb-1">
                                                                <span className="text-sm font-semibold text-slate-900 dark:text-white truncate pr-2" title={String(n.title || '')}>
                                                                    {n.title}
                                                                </span>
                                                                <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                                                    {new Date(n.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">
                                                                {n.sender?.profile
                                                                    ? `From: ${n.sender.profile.first_name} ${n.sender.profile.last_name}`
                                                                    : `From: ${n.sender?.email || 'Admin'}`}
                                                            </p>
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                                                <span className="text-[10px] text-slate-400 italic">Click to view details</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-8 text-center text-slate-500 dark:text-slate-400 text-sm italic">
                                                        No new notices
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Server sync stopped needing re-auth — this used to fail silently:
                    main.cjs correctly detected the expired session and stopped
                    syncing (to avoid hammering the cloud with a dead token), but
                    nothing anywhere in the UI ever surfaced it, so the outbox just
                    grew forever with no visible explanation. Shown on every screen
                    size (unlike the top-bar pills below, which only show at xl:),
                    since this needs the admin's attention regardless of window size. */}
                {exeRuntime && exeSyncStatus?.sessionExpired && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm shadow-sm dark:border-rose-900 dark:bg-rose-950/30 lg:px-6">
                        <p className="font-semibold text-rose-800 dark:text-rose-200">
                            ⚠ Server sync stopped — the sign-in session used for cloud sync has expired. Local changes are queuing up but not reaching the cloud.
                        </p>
                        <button type="button" onClick={handleLogout} className="rounded-full bg-rose-700 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-800">
                            Sign in again
                        </button>
                    </div>
                )}

                {/* Page Content */}
                <main className="p-4 lg:px-0 lg:py-6">
                    {/* Outside the error boundary on purpose: if a page throws,
                        the operator must still be able to install the update
                        that may well be what fixes it. */}
                    <UpdateReadyBanner />
                    {/* Inside the boundary, so a page that throws cannot take the
                        sidebar and every other page down with it -- which in the
                        EXE means a blank window with no way back. */}
                    <PageErrorBoundary routeKey={location.pathname}>
                        <Outlet />
                    </PageErrorBoundary>
                </main>
            </div>

            <ReportAdminModal
                open={showReport}
                onClose={() => setShowReport(false)}
                source={exeRuntime ? 'server_exe' : 'institute'}
                defaultInstituteName={reportInstitute.name}
                defaultInstituteId={reportInstitute.id ?? null}
                defaultName={(user as any)?.profile?.first_name ? `${(user as any).profile.first_name} ${(user as any).profile?.last_name || ''}`.trim() : undefined}
                defaultEmail={(user as any)?.email}
            />
        </div>
    );
}
